import { createContext, type ReactNode, useContext, useMemo } from "react";

import { type VariableValue } from "@doitintl/cmp-models";

import { useGlobalVariables, useLocalVariables } from "../../GlobalVariables/VariableManager";

export interface VariableContext {
  globalVariables: VariableValue[];
  localVariables: VariableValue[];
  systemVariables: VariableValue[];
  isLoading: boolean;
  error: Error | undefined;
}

interface VariableProviderProps {
  children: ReactNode;
  flowId?: string;
}

const VariableContext = createContext<VariableContext | undefined>(undefined);

export const VariableProvider = ({ children, flowId }: VariableProviderProps) => {
  const { globalVariables, loading: globalLoading, error: globalError } = useGlobalVariables();
  const { localVariables, loading: flowLoading, error: flowError } = useLocalVariables(flowId || "");

  const contextValue = useMemo(
    () => ({
      globalVariables: globalVariables ?? [],
      localVariables: localVariables ?? [],
      systemVariables: [],
      isLoading: globalLoading || flowLoading,
      error: globalError || flowError,
    }),
    [globalVariables, localVariables, globalLoading, flowLoading, globalError, flowError]
  );

  return <VariableContext.Provider value={contextValue}>{children}</VariableContext.Provider>;
};

export const useVariableContext = () => {
  const context = useContext(VariableContext);
  if (!context) {
    throw new Error("useVariableContext must be used within a VariableProvider");
  }
  return context;
};
